import React, { useState, useEffect, useMemo, useRef } from 'react';
import { addDays, addWeeks, addMonths, addHours } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WorkOrdersTable } from './components/work-orders/WorkOrdersTable';
import { assetService } from '../../services/activos/assetService';
import { ExecutionDetails } from './components/execution/ExecutionDetails';
import { PlansModal } from './components/plans/PlansModal';
import { maintPlanService } from './services/maintPlanService';
import { workOrdersService } from './services/workOrdersService';
import { ExecutionModal } from './components/execution/ExecutionModal';
import { ExecutionCalendarView } from './components/execution/ExecutionCalendarView';
import { ExecutionWOInfoCard } from './components/execution/ExecutionWOInfoCard';
import { Loader2, AlertCircle, List, SquareKanban, Package, Search, X, Funnel, Calendar } from 'lucide-react';
import { Badge } from '../../components/ui/Badge';
import { FilterSelect } from '../../components/filters/FilterSelect';
import { useFactory } from '../../context/FactoryContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useCurrentEmployee } from '../../hooks/useCurrentEmployee';
import { ReportModal } from './components/execution/ReportModal';
import { PlansFiltersBar } from '../../components/filters/PlansFiltersBar';
import { ExecutionModalReschedule } from './components/execution/ExecutionModalReschedule';

const DEFAULT_PAGE_SIZE = 5;
const PAGE_SIZE_OPTIONS = [5, 10];

const PlanesEjecucion = () => {
    const currentEmployee = useCurrentEmployee();
    const { isLoading: isLoadingAuth } = currentEmployee;
    const userID = currentEmployee?.employeeId;

    const [selectedStatusMobile, setSelectedStatusMobile] = useState('scheduled');
    const [showFiltersMobile, setShowFiltersMobile] = useState(false);

    const queryClient = useQueryClient();
    const { hasAnyRoleKey, isLoading: isLoadingPermissions } = usePermissions();
    const [searchQuery, setSearchQuery] = useState('');
    const [filterAsset, setFilterAsset] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterMaintenanceType, setFilterMaintenanceType] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(true);
    const [selectedWoId, setSelectedWoId] = useState(null);
    const [selectedWO, setSelectedWO] = useState(null);
    const [draftWO, setDraftWO] = useState(null);
    const [woToDelete, setWoToDelete] = useState(null);
    const { selectedFactory, factories, factoryCodes } = useFactory();

    // Work Orders specific state
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedPlanId, setSelectedPlanId] = useState(null);
    const [searchQueryWO, setSearchQueryWO] = useState('');
    const [filterAssetWO, setFilterAssetWO] = useState('');
    const [filterPriorityWO, setFilterPriorityWO] = useState('');
    const [filterMaintenanceTypeWO, setFilterMaintenanceTypeWO] = useState('');
    const [filterStatusWO, setFilterStatusWO] = useState('');
    const [woPage, setWoPage] = useState(1);
    const [woPageSize, setWoPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [viewMode, setViewMode] = useState('table');

    const formRef = useRef(null);

    const isSuperior = hasAnyRoleKey(
        'MAIN:Supervisor',
        'MAIN:Manager',
        'OT:Manager'
    );

    // Fetch maintenance plans
    const {
        data: plansResponse,
        isLoading: isLoadingPlans,
        isError: isErrorPlans,
        error: plansError
    } = useQuery({
        queryKey: ['plans', selectedFactory],
        queryFn: () => maintPlanService.getAll({ factory_code: selectedFactory }, 1, 500),
        retry: 1,
        enabled: isSuperior,
    });
    const plans2 = plansResponse?.data || [];

    // Fetch ALL work orders from selected factory
    const {
        data: workOrdersResponse,
        isLoading: isLoadingWorkOrders,
        isError: isErrorWorkOrders,
        error: workOrdersError,
        refetch: refetchWorkOrders
    } = useQuery({
        queryKey: ['workOrders', selectedFactory],
        queryFn: () => workOrdersService.list({ factory_name: selectedFactory }, 1, 100),
        retry: 1,
    });

    // Fetch one single WO
    const {
        data: woResponse,
        isLoading: isLoadingWo,
        isError: isErrorWo,
        error: woError
    } = useQuery({
        queryKey: ['wo', selectedWoId],
        queryFn: () => workOrdersService.getById(selectedWoId),
        retry: 1,
        enabled: !!selectedWoId,
    });

    useEffect(() => {
        if (woResponse) {
            setSelectedWO(woResponse);
        }
    }, [woResponse]);
    const allWorkOrders = isSuperior ? (workOrdersResponse?.data || []) : (workOrdersResponse?.data || []).filter(wo => wo.assignments?.some(a => a.employee_id === parseInt(userID)));

    // Filter work orders based on selected plan and filters
    const filteredWorkOrders = useMemo(() => {
        let filtered = allWorkOrders;


        // Filter by selected plan
        if (selectedPlanId) {
            filtered = filtered.filter(wo => wo.maintenance_plan_id === parseInt(selectedPlanId));
        }

        // Apply search query
        if (searchQueryWO) {
            const query = searchQueryWO.toLowerCase();
            filtered = filtered.filter(wo =>
                wo.title?.toLowerCase().includes(query) ||
                wo.work_order_type?.toLowerCase().includes(query) ||
                wo.priority?.toLowerCase().includes(query) ||
                wo.description?.toLowerCase().includes(query)
            );
        }

        // Apply asset filter
        if (filterAssetWO) {
            filtered = filtered.filter(wo => wo.asset_name === filterAssetWO);
        }

        // Apply priority filter
        if (filterPriorityWO) {
            filtered = filtered.filter(wo => wo.priority === filterPriorityWO);
        }

        // Apply maintenance type filter
        if (filterMaintenanceTypeWO) {
            filtered = filtered.filter(wo => wo.work_order_type === filterMaintenanceTypeWO);
        }

        // Apply status filter
        if (filterStatusWO) {
            filtered = filtered.filter(wo => wo.status === filterStatusWO);
        }
        const pending_wo = filtered.filter(wo => wo.status === 'pending');
        const scheduled_wo = filtered.filter(wo => new Date(wo.scheduled_date).setHours(0, 0, 0, 0) >= new Date().setHours(0, 0, 0, 0) && wo.status === 'scheduled');
        const in_progress_wo = filtered.filter(wo => wo.status === 'in_progress');
        const completed_wo = filtered.filter(wo => wo.status === 'completed');
        const cancelled_wo = filtered.filter(wo => new Date(wo.scheduled_date).setHours(0, 0, 0, 0) < new Date().setHours(0, 0, 0, 0) && wo.status === 'scheduled');

        return [pending_wo, scheduled_wo, in_progress_wo, completed_wo, cancelled_wo];
    }, [allWorkOrders, selectedPlanId, searchQueryWO, filterAssetWO, filterPriorityWO, filterMaintenanceTypeWO, filterStatusWO]);

    useEffect(() =>
        setSelectedPlan(plans2.find(plan => plan.id === parseInt(selectedPlanId))),
        [selectedPlanId, plans2]);


    // Extract unique values for work orders filters
    const uniqueAssetsWO = useMemo(() => {
        const assets = [...new Set(allWorkOrders.map(wo => wo.asset_name).filter(Boolean))].sort();
        return assets;
    }, [allWorkOrders]);

    const uniquePrioritiesWO = useMemo(() => {
        const priorities = [...new Set(allWorkOrders.map(wo => wo.priority).filter(Boolean))].sort();
        return priorities;
    }, [allWorkOrders]);

    const uniqueMaintenanceTypesWO = useMemo(() => {
        const types = [...new Set(allWorkOrders.map(wo => wo.work_order_type).filter(Boolean))].sort();
        return types;
    }, [allWorkOrders]);

    const uniqueStatusesWO = useMemo(() => {
        const statuses = [...new Set(allWorkOrders.map(wo => wo.status).filter(Boolean))].sort();
        return statuses;
    }, [allWorkOrders]);

    // Pagination for work orders
    const totalWOTems = filteredWorkOrders.flat().length;
    const totalWOPages = Math.max(1, Math.ceil(totalWOTems / woPageSize));
    const paginatedWorkOrders = useMemo(
        () => filteredWorkOrders.flat().slice((woPage - 1) * woPageSize, woPage * woPageSize),
        [filteredWorkOrders, woPage, woPageSize]
    );
    const woRangeStart = totalWOTems === 0 ? 0 : (woPage - 1) * woPageSize + 1;
    const woRangeEnd = Math.min(woPage * woPageSize, totalWOTems);

    // Reset page when filters change
    useEffect(() => {
        setWoPage(1);
    }, [searchQueryWO, filterAssetWO, filterPriorityWO, filterMaintenanceTypeWO, selectedPlanId, woPageSize]);

    // Extract unique values for maintenance plan filters
    const uniqueAssets = useMemo(() => {
        const assets = [...new Set(plans2.map(p => p.asset_location).filter(Boolean))].sort();
        return assets;
    }, [plans2]);

    const uniquePriorities = useMemo(() => {
        const priorities = [...new Set(plans2.map(p => p.priority).filter(Boolean))].sort();
        return priorities;
    }, [plans2]);

    const uniqueMaintenanceTypes = useMemo(() => {
        const types = [...new Set(plans2.map(p => p.maintenance_type).filter(Boolean))].sort();
        return types;
    }, [plans2]);

    // Filter maintenance plans based on search query and filters
    const filteredPlans = useMemo(() => {
        let filtered = plans2;

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(plan =>
                plan.name?.toLowerCase().includes(query) ||
                plan.maintenance_type?.toLowerCase().includes(query) ||
                plan.priority?.toLowerCase().includes(query) ||
                plan.description?.toLowerCase().includes(query)
            );
        }

        if (filterAsset) {
            filtered = filtered.filter(plan => plan.asset_location === filterAsset);
        }

        if (filterPriority) {
            filtered = filtered.filter(plan => plan.priority === filterPriority);
        }

        if (filterMaintenanceType) {
            filtered = filtered.filter(plan => plan.maintenance_type === filterMaintenanceType);
        }

        return filtered;
    }, [plans2, searchQuery, filterAsset, filterPriority, filterMaintenanceType]);

    const hasActiveFiltersWO = filterPriorityWO || filterMaintenanceTypeWO || filterAssetWO || filterStatusWO;

    const clearFiltersWO = () => {
        setFilterPriorityWO('');
        setFilterMaintenanceTypeWO('');
        setFilterAssetWO('');
        if (setFilterStatusWO) {
            setFilterStatusWO('');
        }

    };

    // Mutations
    const createMutation = useMutation({
        mutationFn: (productData) => workOrdersService.create(productData),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders', selectedFactory] });
            setIsModalOpen(false);
        },
        onError: (error) => {
            console.error('Error creating plan:', error);
            throw error;
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, data }) => workOrdersService.update(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders', selectedFactory] });
            queryClient.invalidateQueries({ queryKey: ['wo', selectedWoId] });
        },
        onError: (error) => {
            console.error('Error updating product:', error);
            throw error;
        },
    });

    const maintPlanCompleteMutation = useMutation({
        mutationFn: ({ id }) => maintPlanService.complete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['plans', selectedFactory] });
        },
        onError: (error) => {
            console.error('Error updating plan:', error);
            throw error;
        },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => workOrdersService.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            setWoToDelete(null);
            if (selectedWoId === woToDelete) {
                setSelectedWoId(null);
            }
        },
        onError: (error) => {
            console.error('Error deleting work order:', error);
            setWoToDelete(null);
            alert(error.message || 'Error deleting work order');
        },
    });

    const completeMutation = useMutation({
        mutationFn: ({ id, data }) => workOrdersService.complete(id, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['workOrders'] });
            queryClient.invalidateQueries({ queryKey: ['wo', selectedWoId] });
        },
        onError: (error) => {
            console.error('Error completing work order:', error);
            throw error;
        },
    })

    // Handlers
    const handleTaskActivation = () => {
        setSelectedWoId(null);
        setIsModalOpen(true);
    };

    const handleDelete = (productId) => {
        if (window.confirm('Are you sure you want to delete this work order? This action cannot be undone.')) {
            setWoToDelete(productId);
            deleteMutation.mutate(productId);
        }
    };

    const handleSaveCreate = async (productData) => {
        await createMutation.mutateAsync(productData);
    };

    const handleSaveUpdate = async (productData) => {
        if (selectedWoId) {
            await updateMutation.mutateAsync({
                id: selectedWoId,
                data: productData
            });
        }
    };

    const handleSaveDuplicate = async (productData) => {
        await createMutation.mutateAsync(productData);
    };

    const handleProductClick = (product) => {
        setSelectedWoId(product.id);
    };

    const handleFinish = async (wo, data) => {
        await completeMutation.mutateAsync({ id: wo.id, data });

        if (wo.maintenance_plan_id != null) {
            await maintPlanCompleteMutation.mutateAsync({ id: wo.maintenance_plan_id });
        }

        if (wo.work_order_type === 'preventive') {
            console.log('Entered WO renewal')
            const plan = await maintPlanService.getById(wo.maintenance_plan_id);
            const frequency_type = plan?.frequency_type;
            const frequency_value = plan.frequency_value;
            let new_wo = { ...wo }
            new_wo.tasks.forEach(task => {
                task.status = 'pending';
                task.completed_at = null;
                task.seconds_worked = 0;
                task.pause_reasons = null;
                task.observations = '';
                task.documents = [];
            });
            new_wo.assignments.forEach(ass => {
                ass.start_time = null;
                ass.end_time = null;
                ass.hours_worked = 0;
            })
            let new_scheduled_date;
            switch (frequency_type) {
                case 'days':
                    new_scheduled_date = addDays(new Date(wo.scheduled_date), frequency_value);
                    break;
                case 'weeks':
                    new_scheduled_date = addWeeks(new Date(wo.scheduled_date), frequency_value);
                    break;
                case 'months':
                    new_scheduled_date = addMonths(new Date(wo.scheduled_date), frequency_value);
                    break;
                case 'hours':
                    new_scheduled_date = addHours(new Date(wo.scheduled_date), frequency_value);
                    break;
            }

            new_wo.scheduled_date = new_scheduled_date.toISOString();
            new_wo.status = 'scheduled';
            setDraftWO(new_wo);
            setIsRescheduleModalOpen(true);
            //await createMutation.mutateAsync(new_wo);
        }

    }

    useEffect(() => {
        if (formRef.current && selectedWO && !isLoadingWo) {
            const timer = setTimeout(() => {
                formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [selectedWO, isLoadingWo]);

    const handleFinishRescheduledWO = async (wo) => {
        await createMutation.mutateAsync(wo)
    }

    const isLoading = isLoadingPlans || isLoadingWorkOrders;
    const isError = isErrorPlans;
    const error = plansError;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
                    <p className="text-gray-400">Loading work orders...</p>
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-red-400 mb-2">Error loading plans</p>
                    <p className="text-gray-400 text-sm">{error?.message || 'Unknown error'}</p>
                </div>
            </div>
        );
    }

    const viewSelector = (() => {
        return (
            <div className="flex relative w-fit bg-gray-800 rounded-t-lg p-1 mb-[-2px] border-t border-l border-r border-gray-700 top-[-1px]  md:top-[-1px]">
                <button
                    onClick={() => setViewMode('board')}
                    className={`px-4 py- rounded-md text-sm font-medium transition-colors ${viewMode === 'board' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                    <SquareKanban className='w-4 h-4' />
                </button>
                <button
                    onClick={() => setViewMode('table')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                    <List className='w-4 h-4' />
                </button>
                <button
                    onClick={() => setViewMode('calendar')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}
                >
                    <Calendar className='w-4 h-4' />
                </button>
            </div>
        );
    })

    return (
        <div className="p-4 sm:p-6 w-full min-w-0 relative">
            <div className="mb-6">
                <h1 className="text-3xl pl-6 font-bold text-white mb-2">Execution</h1>
                <div className="flex justify-between items-center pr-6">
                    <p className="text-gray-400 pl-6">Start and complete work orders.</p>
                </div>
            </div>

            {viewMode === 'calendar' && (
                <div className='pb-6'>
                    <div>
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex gap-2 items-center">
                                <div className="flex items-center gap-2 bg-gray-800/80 rounded-t-lg border-t border-r border-gray-700 pr-2">
                                    {viewSelector()}
                                    <div className="flex">
                                        <button type="button" className="text-white font-bold whitespace-nowrap text-sm rounded-lg px-2 py-1 cursor-pointer"
                                            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                                        >
                                            <Funnel className={`w-4 h-4 ${showFiltersMobile ? 'text-blue-500' : ''}`} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="text-white border border-red-700 font-bold whitespace-nowrap text-sm bg-red-600 rounded-lg px-2 py-1 cursor-pointer hover:bg-red-400 "
                                onClick={() => setIsModalOpen(true)}>
                                Report Incident
                            </button>
                        </div>
                    </div>

                    <div className="bg-gray-800 border border-gray-700 rounded-lg rounded-tl-none pb-2 px-2 pt-2 mb-6">
                        {showFiltersMobile && <div className="pb-4">
                            <PlansFiltersBar
                                searchQuery={searchQueryWO}
                                setSearchQuery={setSearchQueryWO}
                                filterAsset={filterAssetWO}
                                setFilterAsset={setFilterAssetWO}
                                filterPriority={filterPriorityWO}
                                setFilterPriority={setFilterPriorityWO}
                                filterMaintenanceType={filterMaintenanceTypeWO}
                                setFilterMaintenanceType={setFilterMaintenanceTypeWO}
                                uniqueAssets={uniqueAssetsWO}
                                uniquePriorities={uniquePrioritiesWO}
                                uniqueMaintenanceTypes={uniqueMaintenanceTypesWO}
                            />
                        </div>}

                        <ExecutionCalendarView
                            workOrders={filteredWorkOrders.flat()}
                            onWorkOrderClick={(wo) => {
                                setSelectedWO(wo);
                            }}
                        />
                    </div>
                </div>
            )}




            {viewMode === 'board' && (
                <>
                    <div>
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex gap-2 items-center">
                                <div className="flex items-center gap-2 bg-gray-800/80 rounded-t-lg border-t border-r border-gray-700 pr-2">
                                    {viewSelector()}
                                    <div className="flex">
                                        <button type="button" className="text-white font-bold whitespace-nowrap text-sm rounded-lg px-2 py-1 cursor-pointer"
                                            onClick={() => setShowFiltersMobile(!showFiltersMobile)}
                                        >
                                            <Funnel className={`w-4 h-4 ${showFiltersMobile ? 'text-blue-500' : ''}`} />
                                        </button>
                                    </div>

                                </div>
                            </div>
                            <button
                                type="button"
                                className="text-white border border-red-700 font-bold whitespace-nowrap text-sm bg-red-600 rounded-lg px-2 py-1 cursor-pointer hover:bg-red-400 "
                                onClick={() => setIsModalOpen(true)}>
                                Report Incident
                            </button>
                        </div>
                    </div>
                    <div className="bg-gray-800 border border-gray-700 rounded-lg rounded-tl-none pb-2 px-2 pt-2 mb-6">
                        {showFiltersMobile && <div className="pb-4">
                            <PlansFiltersBar
                                searchQuery={searchQueryWO}
                                setSearchQuery={setSearchQueryWO}
                                filterAsset={filterAssetWO}
                                setFilterAsset={setFilterAssetWO}
                                filterPriority={filterPriorityWO}
                                setFilterPriority={setFilterPriorityWO}
                                filterMaintenanceType={filterMaintenanceTypeWO}
                                setFilterMaintenanceType={setFilterMaintenanceTypeWO}
                                uniqueAssets={uniqueAssetsWO}
                                uniquePriorities={uniquePrioritiesWO}
                                uniqueMaintenanceTypes={uniqueMaintenanceTypesWO}
                            />
                        </div>}
                        <div className="flex md:flex-row text-gray-300 gap-2 bg-gray-800   font-medium text-md justify-around max-h-[calc(100vh-15rem)]">

                            <div className={`md:flex-1 flex flex-col gap-2 ${selectedStatusMobile !== 'scheduled' ? 'w-0' : ' w-full'}`}>
                                {/* Desktop header */}
                                <div className="md:flex hidden items-center gap-2 mb-1">
                                    <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
                                    <span className="text-sm font-semibold text-gray-300">Scheduled</span>
                                    <span className="ml-auto text-[10px] font-bold bg-blue-500/15 text-blue-300 border border-blue-500/20 px-1.5 py-0.5 rounded-full">
                                        {filteredWorkOrders[1].length}
                                    </span>
                                </div>
                                {/* Mobile header */}
                                <p className={`border-l-3 border-blue-400 pl-2 md:hidden block cursor-pointer hover:text-white hover:bg-gray-700 ${selectedStatusMobile !== 'scheduled' ? 'text-white/0' : ''}`}
                                    onClick={() => setSelectedStatusMobile('scheduled')}
                                >{selectedStatusMobile === 'scheduled' ? 'Scheduled' : ' +'} </p>
                                <div className={`md:flex ${selectedStatusMobile === 'scheduled' ? 'flex' : 'hidden'} flex-col gap-2 overflow-y-auto scrollbar-none overflow-x-hidden`}>
                                    {filteredWorkOrders[1].sort((a, b) => new Date(b.scheduled_date) < new Date(a.scheduled_date) ? 1 : -1).map((wo) => (
                                        <div key={wo.id} className='cursor-pointer'
                                            onClick={() => handleProductClick(wo)}>
                                            <ExecutionWOInfoCard
                                                workOrder={wo}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`md:flex-1 flex flex-col gap-2 ${selectedStatusMobile !== 'in_progress' ? 'w-0' : 'w-full'}`}>
                                {/* Desktop header */}
                                <div className="md:flex hidden items-center gap-2 mb-1">
                                    <span className="w-2 h-2 rounded-full bg-amber-400 shrink-0" />
                                    <span className="text-sm font-semibold text-gray-300">In Progress</span>
                                    <span className="ml-auto text-[10px] font-bold bg-amber-500/15 text-amber-300 border border-amber-500/20 px-1.5 py-0.5 rounded-full">
                                        {filteredWorkOrders[2].length}
                                    </span>
                                </div>
                                {/* Mobile header */}
                                <p className={`border-l-3 border-yellow-500 pl-2 md:hidden block cursor-pointer hover:text-white hover:bg-gray-700 ${selectedStatusMobile !== 'in_progress' ? 'text-white/0' : ''}`}
                                    onClick={() => setSelectedStatusMobile('in_progress')}
                                >{selectedStatusMobile === 'in_progress' ? "In Progress" : '-'} </p>
                                <div className={`md:flex ${selectedStatusMobile === 'in_progress' ? 'flex' : 'hidden'} flex-col gap-2 overflow-y-auto scrollbar-none overflow-x-hidden`}>
                                    {filteredWorkOrders[2].sort((a, b) => new Date(b.scheduled_date) < new Date(a.scheduled_date) ? 1 : -1).map((wo) => (
                                        <div key={wo.id} className='cursor-pointer'
                                            onClick={() => handleProductClick(wo)}>
                                            <ExecutionWOInfoCard
                                                workOrder={wo}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`md:flex-1 flex flex-col gap-2 ${selectedStatusMobile !== 'completed' ? 'w-0' : 'w-full'}`}>
                                {/* Desktop header */}
                                <div className="md:flex hidden items-center gap-2 mb-1">
                                    <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                                    <span className="text-sm font-semibold text-gray-300">Completed</span>
                                    <span className="ml-auto text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
                                        {filteredWorkOrders[3].length}
                                    </span>
                                </div>
                                {/* Mobile header */}
                                <p className={`border-l-3 border-green-400 pl-2 md:hidden block cursor-pointer hover:text-white hover:bg-gray-700 ${selectedStatusMobile !== 'completed' ? 'text-white/0' : ''}`}
                                    onClick={() => setSelectedStatusMobile('completed')}
                                >{selectedStatusMobile === 'completed' ? "Completed" : ' +'} </p>
                                <div className={`md:flex ${selectedStatusMobile === 'completed' ? 'flex' : 'hidden'} flex-col gap-2 overflow-y-auto scrollbar-none overflow-x-hidden`}>
                                    {filteredWorkOrders[3].sort((a, b) => new Date(b.scheduled_date) > new Date(a.scheduled_date) ? 1 : -1).map((wo) => (
                                        <div key={wo.id} className='cursor-pointer'
                                            onClick={() => handleProductClick(wo)}>
                                            <ExecutionWOInfoCard
                                                workOrder={wo}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={`md:flex-1 flex flex-col gap-2 ${selectedStatusMobile !== 'cancelled' ? 'w-0' : 'w-full'}`}>
                                {/* Desktop header */}
                                <div className="md:flex hidden items-center gap-2 mb-1">
                                    <span className="w-2 h-2 rounded-full bg-red-400/70 shrink-0" />
                                    <span className="text-sm font-semibold text-gray-300">Delayed</span>
                                    <span className="ml-auto text-[10px] font-bold bg-red-500/15 text-red-300 border border-red-500/20 px-1.5 py-0.5 rounded-full">
                                        {filteredWorkOrders[4].length}
                                    </span>
                                </div>
                                {/* Mobile header */}
                                <p className={`border-l-3 border-red-500/80 pl-2 md:hidden block cursor-pointer hover:text-white hover:bg-gray-700 ${selectedStatusMobile !== 'cancelled' ? 'text-white/0' : ''}`}
                                    onClick={() => setSelectedStatusMobile('cancelled')}
                                >{selectedStatusMobile === 'cancelled' ? "Cancelled" : ' +'} </p>
                                <div className={`md:flex ${selectedStatusMobile === 'cancelled' ? 'flex' : 'hidden'} flex-col gap-2 overflow-y-auto scrollbar-none overflow-x-hidden`}>
                                    {filteredWorkOrders[4].sort((a, b) => new Date(b.scheduled_date) > new Date(a.scheduled_date) ? 1 : -1).map((wo) => (
                                        <div key={wo.id} className='cursor-pointer'
                                            onClick={() => handleProductClick(wo)}>
                                            <ExecutionWOInfoCard
                                                workOrder={wo}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>
                    </div>
                </>
            )}

            {viewMode === 'table' &&
                (<div>
                    <div className="flex items-center justify-between">
                        {viewSelector()}
                        <button
                            type="button"
                            className="text-white font-bold text-sm bg-red-600 rounded-lg px-2 py-1 cursor-pointer hover:bg-red-400 "
                            onClick={() => setIsModalOpen(true)}>
                            Report Incident
                        </button>
                    </div>
                </div>
                )}
            <div className={`grid grid-cols-1 ${viewMode === 'table' ? 'md:grid-cols-1' : ''} gap-6 mb-6 items-stretch`}>
                {/* Columna izquierda: Tabla de work orders */}
                {viewMode === 'table' && (
                    <div className="min-w-0 flex flex-col min-h-0">

                        <WorkOrdersTable
                            // Maintenance plan props
                            mode='exec'
                            isSuperior={isSuperior}
                            plans={filteredPlans}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            filterAsset={filterAsset}
                            setFilterAsset={setFilterAsset}
                            filterPriority={filterPriority}
                            setFilterPriority={setFilterPriority}
                            filterMaintenanceType={filterMaintenanceType}
                            setFilterMaintenanceType={setFilterMaintenanceType}
                            uniqueAssets={uniqueAssets}
                            uniquePriorities={uniquePriorities}
                            uniqueMaintenanceTypes={uniqueMaintenanceTypes}
                            filteredPlansCount={filteredPlans.length}

                            // Work orders props
                            workOrders={paginatedWorkOrders}
                            totalItems={totalWOTems}
                            totalPages={totalWOPages}
                            rangeStart={woRangeStart}
                            rangeEnd={woRangeEnd}
                            page={woPage}
                            setPage={setWoPage}
                            pageSize={woPageSize}
                            setPageSize={setWoPageSize}
                            pageSizeOptions={PAGE_SIZE_OPTIONS}
                            searchQueryWO={searchQueryWO}
                            setSearchQueryWO={setSearchQueryWO}
                            filterAssetWO={filterAssetWO}
                            setFilterAssetWO={setFilterAssetWO}
                            filterPriorityWO={filterPriorityWO}
                            setFilterPriorityWO={setFilterPriorityWO}
                            filterMaintenanceTypeWO={filterMaintenanceTypeWO}
                            setFilterMaintenanceTypeWO={setFilterMaintenanceTypeWO}
                            filterStatusWO={filterStatusWO}
                            setFilterStatusWO={setFilterStatusWO}
                            uniqueAssetsWO={uniqueAssetsWO}
                            uniquePrioritiesWO={uniquePrioritiesWO}
                            uniqueMaintenanceTypesWO={uniqueMaintenanceTypesWO}
                            uniqueStatusesWO={uniqueStatusesWO}
                            selectedPlanId={selectedPlanId}
                            setSelectedPlanId={setSelectedPlanId}
                            isLoading={isLoadingWorkOrders}
                            onWorkOrderClick={handleProductClick}
                            selectedWorkOrderId={selectedWoId}
                        />
                    </div>
                )}

                {/* Columna derecha: Detalles del producto */}
                <div ref={formRef} className="min-w-0 flex flex-col min-h-0 md:sticky md:top-6">
                    <ExecutionDetails
                        product={selectedWO}
                        loading={isLoadingWo}
                        onTaskStart={handleTaskActivation}
                        onDelete={handleDelete}
                        onSaveUpdate={handleSaveUpdate}
                        onSaveDuplicate={handleSaveDuplicate}
                        onFinish={handleFinish}
                        isSuperior={isSuperior}
                        userId={userID}
                    />
                </div>
            </div>

            {isModalOpen && (
                <ReportModal
                    isOpen={isModalOpen}
                    userID={userID}
                    onClose={() => {
                        setIsModalOpen(false);
                        if (!selectedWoId) {
                            setSelectedWoId(null);
                        }
                    }}
                    maintenancePlan={selectedPlan}
                    onSave={handleSaveCreate}
                />
            )}

            {isRescheduleModalOpen && draftWO !== null && (
                <ExecutionModalReschedule
                    isOpen={isRescheduleModalOpen}
                    onClose={() => { setIsRescheduleModalOpen(false); setDraftWO(null) }}
                    workOrder={draftWO}
                    setWorkOrder={setDraftWO}
                    onConfirmReschedule={handleFinishRescheduledWO}
                />
            )}
        </div>

    );

};

export default PlanesEjecucion;